import { IAttrValue } from "@multiweb/types/contracts";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { IValidatable } from "../../../UI.BaseControls/ControlInterfaces";
import { Edit } from "../../../UI.BaseControls/Edit";
import { EditableControl } from "../../../UI.BaseControls/EditableControl";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { ElementUtils } from "../../../UI.ElementarControls/Helpers/ElementUtils";
import { Column } from "../Columns/Column";
import { ICellContent } from "../Components/ICellContent";
import { TableCellContent } from "../Components/TableCellContent";
import { ITemplate } from "./ITemplate";

export class EditItemTemplate implements ITemplate {
    private _column: Column;

    public get parentColumn(): Column {
        return this._column;
    }

    public set parentColumn(v: Column) {
        this._column = v;
    }

    public init(): void {
        // Prazdna metoda
    }

    /**
     * Základní renderovaci funkce, ktera se stara o zpracování HTML struktury elementu
     * @param column definice sloupce
     * @param dataSet pole dat
     */
    public render(column: Column, dataSet: IAttrValue[]): ICellContent {
        this.parentColumn = column;
        const wrapper = this._createBasicElement(column);
        const createdElements = this._createEditElement(this.parentColumn, dataSet, wrapper);

        wrapper.appendChild(createdElements.element);

        this.updateData(dataSet, createdElements.element);

        return new TableCellContent(wrapper, createdElements.element, createdElements.validatableControl);
    }

    public updateData(dataSet: IAttrValue[], item: HTMLElement): void {
        const data = this._getDataFromSet(dataSet);
        if (data === undefined)
            return;

        if (!(item instanceof EditableControl))
            return;

        // musi se odregistrovat listener a zmenit hodnota, aby nedoslo k znovuvyvloani udalosti change
        const changeListeners: Array<(e: Event) => void> = item.getEventListeners("change");
        item.unregisterEventListener("change");
        item.value = AttrValueReader.read(data);
        for (const l of changeListeners) {
            item.registerEventListener("change", l);
        }
    }

    public updateDisplayedData(data: ITextBuilder, item: HTMLElement): void {
        if (item instanceof EditableControl) {
            item.displayedValue = data;
        }
    }

    /**
     * Vytvoří hlavní element, do kterého se bude vkládat obsah jednotlivých buněk,
     * @param column obsahuje definici sloupce
     */
    protected _createBasicElement(column: Column): HTMLTableDataCellElement {
        const item: HTMLTableDataCellElement = document.createElement("td");
        item.setAttribute("data-attrName", column.attrName);
        item.setAttribute("data-fullAttrName", column.fullAttrName);
        item.setAttribute("data-displayformat", column.displayFormat);
        item.classList.add("EditableCell");
        item.width = column.width + "px";

        return item;
    }

    /**
     * Vytvoří editovatelnou komponentu, která se vloží do buňky
     * @param column sloupec, ve kterém je buňka
     * @param dataSet zobrazovaná data
     * @param parentItem nadřízený element
     */
    protected _createEditElement(column: Column, dataSet: IAttrValue[], parentItem: HTMLElement): { element: HTMLElement; validatableControl: IValidatable } {
        const editableControl = new Edit();
        editableControl.enabled = false; //předejde se tak možnosti editovat hodnotu dříve než to povolí práva
        editableControl.value = this._getDataFromSet(dataSet);

        // listenery je potřeba vždy po přidání do DOMu znovu zaregistrovat
        editableControl.afterAttach(() => {
            editableControl.registerEventListener("change", (e) => {
                e.stopPropagation();
                ElementUtils.callEvent(parentItem, "valuechanged", { value: editableControl.value, column: column });
            });

            editableControl.registerEventListener("click", (e: MouseEvent) => EventHelper.stopPropagation(e));
        });
        editableControl.isEditing = true;

        return { element: editableControl as HTMLElement, validatableControl: editableControl };
    }

    /**
     * Vrati spojeny a naformatovany retezec dat, zavola formatovaci funkci ze sloupce
     * @param data pole dat
     * @param column Definice sloupce
     */
    protected async _getJoinData(data: IAttrValue | IAttrValue[], column?: Column): Promise<string> {
        if (data instanceof Array) {
            if (column && column.formatValue instanceof Function) {
                let r = "";
                let i = 0;
                for (const d of data) {
                    if (i++ > 0)
                        r += " | ";
                    r += await column.formatValue(d);
                }
                return r;
            } else {
                return data.map((v) => AttrValueReader.read(v)).join(" | ");
            }
        } else {
            if (column && column.formatValue instanceof Function)
                return await column.formatValue(data);
            else
                return data.toString();
        }
    }

    protected _getDataFromSet(dataSet: IAttrValue | IAttrValue[]): IAttrValue {
        return dataSet instanceof Array ? (dataSet.length > 0 ? dataSet[0] : null) : dataSet;
    }

    public dispose(): void {
        // Prazdna metoda
    }
}
